import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import {
  HelpCircle,
  Ticket,
  BookOpen,
  Mail,
  Phone,
  Clock,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import Button from '../components/ui/button';

const Help = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqs = [
    {
      id: 1,
      question: 'How do I create a ticket?',
      answer: 'To create a ticket, click on "Create Ticket" in the sidebar or use the quick action card on this page. Fill in the required fields including subject, description, category, and priority. Once submitted, your ticket will be assigned a unique ticket number and you\'ll receive a confirmation.'
    },
    {
      id: 2,
      question: 'How do I track ticket status?',
      answer: 'You can track your ticket status by navigating to the Tickets section in the sidebar. Your tickets are organized by status: Open, In Progress, Resolved, and Closed. Click on any ticket to view detailed information, comments, and activity history.'
    },
    {
      id: 3,
      question: 'Who can assign tickets?',
      answer: 'Only administrators can assign tickets to specific agents or employees. When you create a ticket, it can be automatically assigned to an assignment group, or an admin can manually assign it to a team member based on expertise and workload.'
    },
    {
      id: 4,
      question: 'How do notifications work?',
      answer: 'You\'ll receive notifications when: a ticket is assigned to you, someone comments on your ticket, ticket status changes, or important updates occur. Notifications appear in the notification bell icon in the header and can be viewed in the Notifications page.'
    },
    {
      id: 5,
      question: 'What is the difference between resolved and closed tickets?',
      answer: 'A resolved ticket means the issue has been fixed and is awaiting your confirmation. A closed ticket means the issue is fully resolved and confirmed, or the ticket has been closed for other reasons. You can reopen a resolved ticket if needed.'
    },
    {
      id: 6,
      question: 'How do I add attachments to a ticket?',
      answer: 'When creating or updating a ticket, you can attach files using the attachment field. Supported file types include images, documents, and other common formats. Attachments help provide context and can speed up resolution time.'
    }
  ];

  const toggleFaq = (id) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  return (
    <DashboardLayout userRole={user?.role || 'customer'}>
      <div className="min-h-full">
        {/* Unified Header */}
        <div className="mb-8">
          <div className="rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-500/20 dark:to-purple-500/20 shadow-lg border border-white/10 dark:border-slate-700 p-8 md:p-10 relative overflow-hidden">
            {/* Decorative glow effect */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none"></div>
            
            <div className="relative flex items-center gap-4">
              <div className="p-4 rounded-xl bg-white/20 dark:bg-indigo-500/30 backdrop-blur-sm">
                <HelpCircle className="w-8 h-8 md:w-10 md:h-10 text-white dark:text-indigo-300" />
              </div>
              <div className="flex-1">
                <h1 className="text-3xl md:text-4xl font-bold text-white dark:text-indigo-100 mb-2">
                  Help & Support
                </h1>
                <p className="text-indigo-100 dark:text-indigo-300 text-sm md:text-base">
                  Get help, raise tickets, or contact support
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Action Cards */}
        <div className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Create Ticket Card */}
            <div className="group relative rounded-2xl border border-gray-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-500 shadow-md">
                  <Ticket className="w-6 h-6 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Create a Ticket
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Submit a new support request or report an issue. Our team will respond promptly.
              </p>
              <Button
                onClick={() => navigate('/tickets/create')}
                className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
              >
                Create Ticket
              </Button>
            </div>

            {/* Knowledge Base Card */}
            <div className="group relative rounded-2xl border border-gray-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 shadow-md">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Browse Knowledge Base
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Search through articles, FAQs, and guides to find instant answers to common questions.
              </p>
              <Button
                onClick={() => navigate('/customer/knowledge')}
                variant="unstyled"
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
              >
                View Articles
              </Button>
            </div>
          </div>
        </div>

        {/* Contact Support Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
            Contact Support
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Email Support */}
            <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4 mb-3">
                <div className="p-3 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">Email Support</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <a href="mailto:support@cantiktech.com" className="text-blue-600 dark:text-blue-400 hover:underline">
                  support@cantiktech.com
                </a>
              </p>
            </div>

            {/* Phone Support */}
            <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4 mb-3">
                <div className="p-3 rounded-lg bg-green-100 dark:bg-green-900/30">
                  <Phone className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">Phone Support</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                +91-9136546820
              </p>
            </div>

            {/* Working Hours */}
            <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4 mb-3">
                <div className="p-3 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                  <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">Working Hours</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Mon–Fri, 9 AM – 6 PM
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
            Common Questions
          </h2>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <div
                key={faq.id}
                className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                <button
                  onClick={() => toggleFaq(faq.id)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 dark:text-gray-100 pr-4">
                    {faq.question}
                  </span>
                  <div className="flex-shrink-0">
                    {expandedFaq === faq.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    )}
                  </div>
                </button>
                {expandedFaq === faq.id && (
                  <div className="px-6 pb-4 pt-0">
                    <div className="border-t border-gray-200 dark:border-slate-700 pt-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Help;
